// Lib
import React, { useEffect, useState, useRef, useCallback } from 'react';
import Handsontable from 'handsontable/base';
import { isNil } from 'lodash';

// Utils
import { getTableScrollableElement } from '../utils/tableDOMUtils';
import useThrottledCallback from '../../../utils/react/useThrottledCallback';
import { isInDocumentModeList, isInList } from '../../../../common/inList/inListUtils';

interface Props {
    isSingleSelected: boolean;
    isDragging: boolean;
    hotTableInstanceRef: React.MutableRefObject<Handsontable.Core | null>;
    hotTableContainerRef: React.MutableRefObject<HTMLDivElement | null>;
    inList: string | null;
    isReadOnly: boolean;
}

/**
 * Covers the table with an overlay div when it is not selected.
 *
 * Added when element is not selected, in order to prevent editing cell directly and allow dragging
 */
const TableOverlay = (props: Props) => {
    const { isSingleSelected, isDragging, hotTableInstanceRef, hotTableContainerRef, inList, isReadOnly } = props;

    const [showTableOverlay, setShowTableOverlay] = useState(!isSingleSelected);

    const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);
    const touchStartXRef = useRef<number | null>(null);

    const isInDocumentMode = isInDocumentModeList(inList);
    // Is table inside a column/trash/unsorted notes etc where width is constrained
    const inElementList = isInList(inList);

    const tableScrollableElement = getTableScrollableElement(hotTableContainerRef.current);
    const scrollWidth = tableScrollableElement?.scrollWidth;
    const clientWidth = tableScrollableElement?.clientWidth;
    const isScrollable = !isNil(scrollWidth) && !isNil(clientWidth) && scrollWidth > clientWidth;

    useEffect(() => {
        // Always cover table in readonly mode, except if its in mobile view
        setShowTableOverlay(isReadOnly && !isInDocumentMode);
    }, [isReadOnly, isInDocumentMode]);

    useEffect(() => {
        // Set viewportColumnRenderingOffset to 100 JUST AFTER the table has rendered in list view
        // This is to prevent the table from rendering extra columns offscreen when it's first rendered in list view
        // But the correct number of columns will be loaded after the initial render, ready for dragging and scrolling

        // small number of non-visible columns to be rendered to the left of the viewport
        // using 'auto' causes an issue where a table with a small width (<85px) will only render the first column

        if (!inList) return;

        const minOffset = 3;

        setTimeout(() => {
            if (!hotTableInstanceRef.current) return;

            hotTableInstanceRef.current.updateSettings({
                viewportColumnRenderingOffset: inElementList ? 100 : minOffset,
            });
        }, 0);
    }, [inElementList, inList, hotTableInstanceRef]);

    // Handle isSingleSelected changes
    useEffect(() => {
        // When isSingleSelected prop has changed, wait for a bit before covering table with overlay, this is to
        // allow all the necessary functions to complete before the table is covered
        if (isSingleSelected) {
            setTimeout(() => {
                setShowTableOverlay(false);
            }, 0);
        } else {
            setShowTableOverlay(true);
        }
    }, [isSingleSelected]);

    // This function temporarily removes the table overlay to allow scrolling
    // when the user is trying to scroll a table in column, and prevents horizontal canvas scrolling
    const updateTableOverlay = useThrottledCallback(
        () => {
            // We should only run this function if the table can scroll, and the user is trying to scroll it
            if (isSingleSelected || !isScrollable || !inElementList || isDragging) return;

            const canvasViewport = document.querySelector('.CanvasViewport');

            if (showTableOverlay) {
                // Prevent canvas scrolling
                canvasViewport?.classList.add('prevent-horizontal-scrolling');
                // Remove overlay to allow scrolling
                setShowTableOverlay(false);
            }

            // If there is an existing timer, clear it
            if (scrollTimerRef.current !== null) clearTimeout(scrollTimerRef.current);

            // Set a timer to cover the table and reset the canvas scroll after a short delay
            scrollTimerRef.current = setTimeout(() => {
                canvasViewport?.classList.remove('prevent-horizontal-scrolling');
                setShowTableOverlay(true);
            }, 150);
        },
        100,
        [isSingleSelected, inElementList, isDragging, showTableOverlay, isScrollable],
    );

    const onWheel = useCallback(
        (event: React.WheelEvent) => {
            // If the user is scrolling vertically, don't do anything
            if (event.deltaX === 0) return;

            updateTableOverlay();
        },
        [updateTableOverlay],
    );

    const onTouchStart = useCallback((event: React.TouchEvent) => {
        touchStartXRef.current = event.changedTouches[0].clientX;
    }, []);

    const onTouchMove = useCallback(
        (event: React.TouchEvent) => {
            // If the user is scrolling vertically, don't do anything
            if (event.changedTouches[0].clientX === touchStartXRef.current) return;

            updateTableOverlay();
        },
        [updateTableOverlay],
    );

    if (!showTableOverlay) return null;

    // If element not selected, add cover over table in order to prevent editing and allow dragging
    return <div className="TableOverlay" onWheel={onWheel} onTouchStart={onTouchStart} onTouchMove={onTouchMove}></div>;
};

export default TableOverlay;
